/**
 * 统一数据映射层 - 从数据库 snake_case 字段到前端标准格式
 *
 * 核心规范：
 * 1. title = 短题目名称（统一语义）
 * 2. content = 完整富文本题干（统一语义）
 * 3. 图片统一为 ImageItem 对象数组（消除双数组索引同步 Bug）
 * 4. 已清理 questionTitle / questionStem / name 等冗余字段，不再输出，不再输出
 */

import {
  ImageItem,
  Option,
  QuestionType,
  Difficulty,
  mergeImageArrays,
  convertLegacyOption,
  isObjectiveQuestion,
  isCodingQuestion,
} from '../types/question';
import { extractImageWidths } from './questionHtmlProcessor';

// 布局值映射：数据库值 → 前端标准值
const TITLE_LAYOUT_MAP: Record<string, string> = {
  'auto': 'auto',
  'cols1': 'row',
  'cols2': 'col-2',
  'cols3': 'col-3',
  'cols4': 'col-4',
  'row': 'row',
  'col-2': 'col-2',
  'col-3': 'col-3',
  'col-4': 'col-4',
};

/**
 * 构建标准字段对象
 * 所有题型分支都需要附带这些字段
 */
function buildStandardFields(
  title: string,
  content: string,
  titleImages: ImageItem[],
  globalOptionWidths: Record<string, number>,
  options: Option[],
): Record<string, any> {
  // 将新格式 Option[] 转换为前端渲染格式（含 image/images/imageWidth/imageWidths）
  const standardOptions = options.map(opt => ({
    id: opt.id,
    label: opt.label,
    text: opt.text,
    isCorrect: opt.isCorrect,
    // 单图兼容
    image: opt.images.length === 1 ? opt.images[0].url : undefined,
    imageWidth: opt.images.length === 1 ? opt.images[0].widthPercent : undefined,
    // 多图支持
    images: opt.images.length > 0 ? opt.images.map(i => i.url) : undefined,
    imageWidths: opt.images.length > 0 ? opt.images.map(i => i.widthPercent) : undefined,
  }));

  // 从 ImageItem[] 中提取宽度数组，供旧组件兼容
  // 优先级：HTML content 中的 data-edit-w > ImageItem.widthPercent
  // 这样编辑器拖拽缩放后保存的尺寸能正确同步到 titleImageWidths
  const htmlWidths = content ? extractImageWidths(content) : [];
  let titleImageWidths = htmlWidths.length > 0
    ? htmlWidths
    : titleImages.map(i => i.widthPercent);

  // 如果所有图片宽度都是100（未设置过的默认值），则设为50作为合理默认值
  // 这样上传解析后的题目在预览模式下也能正常显示
  // 注意：仅当没有 HTML data-edit-w 时才应用此默认值
  if (htmlWidths.length === 0 && titleImageWidths.length > 0 && titleImageWidths.every(w => w === 100)) {
    titleImageWidths = titleImageWidths.map(() => 50);
  }

  return {
    title,
    content,
    titleImages,
    titleImageWidths,
    options: standardOptions,
    optionImageWidths: globalOptionWidths,
  };
}

/**
 * 将数据库原始题目数据映射为前端标准格式
 * 
 * @param dbQ - 数据库中的题目数据（snake_case 字段）
 * @returns 前端标准格式的题目数据（同时包含新旧字段）
 */
export function mapDbQuestionToStandard(dbQ: any): any {
  // 1. 统一 title = 短题目名称
  const title = dbQ.title ?? '';

  // 2. 统一 content = 完整富文本题干
  // 严格所见即所得：mapper 不再对编程题 HTML 做任何规范化（含序号分行、图文分行），
  // 完全按数据库存储的原始 HTML 透传，由下游组件按需渲染。
  const questionType = dbQ.question_type || dbQ.type || 'single_choice';
  const rawContent = dbQ.content || '';
  const content = rawContent;

  // 3. 统一图片结构：合并双数组为 ImageItem[]
  //    编程题图片嵌在 content HTML 中，title_images 可能为空
  //    此时从 content 中提取图片 URL，与 title_image_widths 配对
  const rawTitleImageUrls = dbQ.title_images || dbQ.titleImages;
  const rawTitleImageWidths = dbQ.title_image_widths || dbQ.titleImageWidths;
  let titleImageUrls = rawTitleImageUrls;

  // 编程题场景：title_images 为空但 content 内嵌图片，从 content 提取 URL
  if ((!rawTitleImageUrls || rawTitleImageUrls.length === 0) && rawTitleImageWidths && rawTitleImageWidths.length > 0) {
    const imgRegex = /<img[^>]*src=["']([^"']+)["'][^>]*>/gi;
    const extractedUrls: string[] = [];
    let match: RegExpExecArray | null;
    while ((match = imgRegex.exec(content)) !== null) {
      extractedUrls.push(match[1]);
    }
    if (extractedUrls.length > 0) {
      titleImageUrls = extractedUrls;
    }
  }

  const titleImages: ImageItem[] = mergeImageArrays(
    titleImageUrls,
    rawTitleImageWidths
  );

  // 4. 统一选项图片结构
  const globalOptionWidths = dbQ.option_image_widths || {};
  const options: Option[] = (dbQ.options || []).map((opt: any) =>
    convertLegacyOption(opt)
  );

  // 5. 统一其他图片字段
  const referenceAnswerImages: ImageItem[] = mergeImageArrays(
    dbQ.reference_answer_images || dbQ.referenceAnswerImages,
    dbQ.reference_answer_image_widths || dbQ.referenceAnswerImageWidths
  );
  const scoringPointsImages: ImageItem[] = mergeImageArrays(
    dbQ.scoring_points_images || dbQ.scoringPointsImages,
    dbQ.scoring_points_image_widths || dbQ.scoringPointsImageWidths
  );
  const answerImages: ImageItem[] = mergeImageArrays(
    dbQ.answer_images || dbQ.answerImages,
    dbQ.answer_image_widths || dbQ.answerImageWidths
  );

  // 6. 映射题干布局值
  const titleImageLayout = TITLE_LAYOUT_MAP[dbQ.title_image_layout || 'auto'] || 'auto';

  // 7. 构建标准字段（所有题型都需要）
  const standard = buildStandardFields(title, content, titleImages, globalOptionWidths, options);

  // 8. 公共字段 + 标准字段
  const result: Record<string, any> = {
    id: dbQ.id,
    type: questionType,
    subType: dbQ.sub_type || dbQ.subType,
    score: dbQ.score || 0,
    difficulty: dbQ.difficulty || 'medium',
    titleImageLayout,
    imagePosition: dbQ.image_position || dbQ.imagePosition || 'below',
    optionLayout: dbQ.option_layout || dbQ.optionLayout || 'grid',
    explanation: dbQ.explanation || '',
    // 图片字段（ImageItem[]）
    referenceAnswerImages,
    scoringPointsImages,
    answerImages,
    // 图片宽度（从 ImageItem[] 提取，供旧组件兼容）
    referenceAnswerImageWidths: referenceAnswerImages.map(i => i.widthPercent),
    scoringPointsImageWidths: scoringPointsImages.map(i => i.widthPercent),
    answerImageWidths: answerImages.map(i => i.widthPercent),
    // 标准字段
    ...standard,
    // 题型专属字段
    // 严格所见即所得：编程题字段（answerText/referenceAnswer/scoringPoints）不再做 HTML 规范化，
    // 完全按数据库原始值透传
    answerText: dbQ.reference_answer || dbQ.answerText || '',
    referenceAnswer: dbQ.reference_answer || dbQ.referenceAnswer || '',
    scoringPoints: dbQ.scoring_points || dbQ.scoringPoints || '',
    codeTemplate: dbQ.code_template || dbQ.codeTemplate || '',
    codeLanguage: dbQ.code_language || dbQ.codeLanguage || '',
    allowTextInput: dbQ.allow_text_input ?? dbQ.allowTextInput ?? true,
    allowImageUpload: dbQ.allow_image_upload ?? dbQ.allowImageUpload ?? false,
    allowFileUpload: dbQ.allow_file_upload ?? dbQ.allowFileUpload ?? false,
    maxTextLength: dbQ.max_text_length || dbQ.maxTextLength || 800,
    maxImageSize: dbQ.max_image_size || dbQ.maxImageSize || 0,
    maxFileSize: dbQ.max_file_size || dbQ.maxFileSize || 0,
    scratchSubtype: dbQ.scratch_subtype || dbQ.scratchSubtype || dbQ.sub_type || dbQ.subType,
    referenceSb3Url: dbQ.reference_sb3_url || dbQ.referenceSb3Url,
    scratchAnswerItems: dbQ.scratch_answer_items || dbQ.scratchAnswerItems,
    scratchConfig: dbQ.scratch_config || dbQ.scratchConfig,
    gradingMode: dbQ.grading_mode || dbQ.gradingMode || 'ai',
    testCases: dbQ.test_cases || dbQ.testCases || [],
    structuredScoring: dbQ.structured_scoring || dbQ.structuredScoring,
    timeLimitMs: dbQ.time_limit_ms || dbQ.timeLimitMs || 1000,
    memoryLimitMb: dbQ.memory_limit_mb || dbQ.memoryLimitMb || 128,
    knowledgePointIds: dbQ.knowledge_point_ids || dbQ.knowledgePointIds || [],
    commonMistakes: dbQ.common_mistakes || dbQ.commonMistakes || '',
    pythonEditorFields: dbQ.python_editor_fields || dbQ.pythonEditorFields,
    graphicsContent: dbQ.graphics_content || dbQ.graphicsContent,
    graphicsDifficulty: dbQ.graphics_difficulty || dbQ.graphicsDifficulty || 'medium',
    graphicsCategory: dbQ.graphics_category || dbQ.graphicsCategory || '',
    graphicsAnswerItems: dbQ.graphics_answer_items || dbQ.graphicsAnswerItems,
    demoCode: dbQ.demo_code || dbQ.demoCode || '',
  };

  return result;
}

/**
 * 根据布局值和图片数量计算 grid 列数
 */
export function getTitleImageGridCols(layout: string, imageCount: number): number {
  if (imageCount <= 1) return 1;
  switch (layout) {
    case 'row': return imageCount;
    case 'col-2': return Math.min(2, imageCount);
    case 'col-3': return Math.min(3, imageCount);
    case 'auto':
    default:
      return imageCount <= 2 ? Math.min(2, imageCount) : Math.min(3, imageCount);
  }
}

/**
 * 批量映射数据库题目数据为前端标准格式
 */
export function mapDbQuestionsToStandard(dbQuestions: any[]): any[] {
  return dbQuestions.map(mapDbQuestionToStandard);
}

/**
 * 从数据库补充布局/缩放字段到已有的题目数据
 */
export function enrichQuestionsWithDbFields(questions: any[], dbFields: any[]): any[] {
  const questionMap = new Map(dbFields.map((q: any) => [q.id, q]));

  return questions.map((q: any) => {
    const dbQ = questionMap.get(q.id);
    if (!dbQ) return q;

    // 合并图片数据
    const titleImages: ImageItem[] = mergeImageArrays(
      dbQ.title_images || q.titleImages,
      dbQ.title_image_widths || q.titleImageWidths
    );

    const globalOptionWidths = dbQ.option_image_widths || {};
    const mappedOptions = (q.options || []).map((opt: any) =>
      convertLegacyOption(opt)
    );

    const standard = buildStandardFields(
      dbQ.title ?? q.title ?? '',
      dbQ.content || q.content || '',
      titleImages,
      globalOptionWidths,
      mappedOptions
    );

    return {
      ...q,
      ...standard,
      titleImageLayout: TITLE_LAYOUT_MAP[dbQ.title_image_layout || 'auto'] || q.titleImageLayout || 'auto',
      imagePosition: dbQ.image_position || q.imagePosition || 'below',
      optionLayout: dbQ.option_layout || q.optionLayout || 'grid',
    };
  });
}
